import { useAppDispatch, useAppSelector } from '@/store';
import { JobDetails, setJobDetails } from '@/reducers/jbpGeneralSlice';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Typography } from '@/components/Typography';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useState, useMemo } from 'react';
import { getBookingSuccessType } from '../../../../../APIDocs/apiTypes';

export interface props {
  bookingId: string;
  mpan: string;
  serviceLine: string;
  booking: getBookingSuccessType;
}

export const RebookJobNew = (props: props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isInternalUser, isSupplierUser } = useUserInfo();
  const { site } = useAppSelector(state => state.jbp);

  const [showRebookModal, setShowRebookModal] = useState(false);
  const modalTitle = 'Rebook Job';

  const canRebook = useMemo(() => {
    if (!props.booking.JobTypeCode) return false;
    const splitJobCode = props.booking.JobTypeCode.split('_');
    if (splitJobCode.length < 2) return false;
    if (['DNO', 'BNO'].includes(props.serviceLine)) {
      return site?.siteType === splitJobCode[0];
    }
    const bookingSiteType = `${splitJobCode[0]} ${splitJobCode[1]}`;
    return site?.siteType === bookingSiteType;
  }, [props.booking.JobTypeCode, props.serviceLine, site?.siteType]);

  const navigateToGetOffers = () => {
    dispatch(
      setJobDetails({
        jobTypeCode: props.booking.JobTypeCode,
        siteAddress: {
          addressName: props.booking.AddressDetails.AddressName ?? '',
          addressLine1: props.booking.AddressDetails.AddressLine1 ?? '',
          addressLine2: props.booking.AddressDetails.AddressLine2 ?? '',
          addressLine3: props.booking.AddressDetails.AddressLine3 ?? '',
          town: props.booking.AddressDetails.Town ?? '',
          county: props.booking.AddressDetails.County ?? '',
          postCode: props.booking.AddressDetails.PostCode ?? ''
        },
        siteContactDetails: {
          title: props.booking.SiteContactDetails?.Title ?? '',
          contactFirstName: props.booking.SiteContactDetails?.ContactFirstName ?? '',
          contactSurname: props.booking.SiteContactDetails?.ContactSurname ?? '',
          emailAddress: props.booking.SiteContactDetails?.EmailAddress ?? '',
          contactNumber: props.booking.SiteContactDetails?.ContactNumber ?? ''
        },
        extraSkills: props.booking.ExtraSkills,
        jobComments: props.booking.JobComments ?? '',
        relatedBookingId: props.bookingId
      } as JobDetails)
    );
    if (isInternalUser) {
      navigate(`/bookJob/internalOffers/${props.mpan}/${props.serviceLine}`);
    } else if (isSupplierUser) {
      navigate(`/bookJob/offers/${props.mpan}/${props.serviceLine}`);
    }
  };

  return (
    <div>
      <Button
        text="Rebook Job"
        primary={true}
        onClick={() => {
          setShowRebookModal(true);
        }}
        disabled={false}
        className="mt-2 inline-block"
      />
      {showRebookModal && (
        <Modal title={modalTitle}>
          <Typography
            content={
              canRebook
                ? `Confirm you wish to rebook this job. All details/comments from this job will be carried over to the new booking.`
                : `The job you're attempting to rebook does not match the current site type ${site?.siteType}. To progress, please raise a new job against the correct site type.`
            }
            className="mb-3"
          />
          <Button primary={false} text="OK" className="mr-4 mt-5" disabled={!canRebook} onClick={() => navigateToGetOffers()} />
          <Button primary={false} text="Cancel" className="mr-4 mt-5" disabled={false} onClick={() => setShowRebookModal(false)} />
        </Modal>
      )}
    </div>
  );
};
